import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaComments } from 'react-icons/fa';

const LoginSectionContainer = styled.section`
  width: 100%;
  padding: 100px 0;
  margin-top: 50px;
  background-color: #000000 !important;
  position: relative;
  overflow: hidden;
  cursor: none;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 30% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 70% 80%, rgba(74, 144, 226, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }

  &:hover {
    background: linear-gradient(135deg, #1a1a1a 0%, #000000 100%) !important;
    border: 2px solid rgba(74, 144, 226, 0.8);
    box-shadow: 0 0 30px rgba(74, 144, 226, 0.3);
  }
`;

const Spotlight = styled.div`
  position: absolute;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(74, 144, 226, 0.15) 0%, transparent 70%);
  border-radius: 50%;
  pointer-events: none;
  z-index: 2;
  transition: all 0.1s ease;
  transform: translate(-50%, -50%);
  opacity: 0;
  
  ${LoginSectionContainer}:hover & {
    opacity: 1;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  width: 100%;
  margin-top: 40px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const FormContainer = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #0f0f0f);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.8s ease-out 0.4s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 107, 53, 0.1), transparent);
    transition: left 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const InfoContainer = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #0f0f0f);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.8s ease-out 0.6s both;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(74, 144, 226, 0.1), transparent);
    transition: left 0.6s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const FormTitle = styled.h3`
  font-size: 1.8rem;
  color: #ffffff;
  margin-bottom: 30px;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 25px;
`;

const Label = styled.label`
  display: block;
  color: #ffffff;
  margin-bottom: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 0.95rem;
  
  .required {
    color: #ff4444;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
  }
`;

const CharCount = styled.div`
  text-align: right;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  margin-top: 5px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 15px;
  background: linear-gradient(45deg, #4a90e2, #3a7bc8);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Roboto', sans-serif;
  
  &:hover {
    background: linear-gradient(45deg, #3a7bc8, #2d5f9e);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(74, 144, 226, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.8rem;
  color: #4a90e2;
  margin-bottom: 15px;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
`;

const InfoSubtitle = styled.p`
  font-size: 1.1rem;
  color: #ffffff;
  margin-bottom: 30px;
  line-height: 1.6;
  font-family: 'Roboto', sans-serif;
`;

const GetInTouch = styled.h4`
  font-size: 1.4rem;
  color: #ffffff;
  margin-bottom: 25px;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateX(5px);
  }
`;

const ContactIcon = styled.div`
  font-size: 1.2rem;
  color: #4a90e2;
  margin-right: 15px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(74, 144, 226, 0.1);
  border-radius: 50%;
`;

const ContactText = styled.div`
  color: #ffffff;
  font-family: 'Roboto', sans-serif;
  font-size: 1rem;
`;

const ChatIcon = styled.div`
  position: fixed;
  bottom: 30px;
  left: 30px;
  width: 60px;
  height: 60px;
  background: linear-gradient(45deg, #8b5cf6, #a855f7);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(139, 92, 246, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(139, 92, 246, 0.4);
  }
  
  @media (max-width: 768px) {
    bottom: 20px;
    left: 20px;
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }
`;

const LoginSection = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const containerRef = useRef(null);

  const handleMouseMove = (e) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    }
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  return (
    <LoginSectionContainer 
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Spotlight 
        style={{
          left: mousePosition.x,
          top: mousePosition.y,
          opacity: isHovering ? 1 : 0
        }}
      />
      <ContentWrapper>
        <ContactGrid>
          <FormContainer>
            <FormTitle>Contact Us</FormTitle>
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label>
                  Name <span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>
                  Email Address <span className="required">*</span>
                </Label>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter Your Email Id"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Enter Your Contact No."
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Message</Label>
                <TextArea
                  name="message"
                  placeholder="Enter your message..."
                  value={formData.message}
                  onChange={handleInputChange}
                  maxLength={180}
                />
                <CharCount>{formData.message.length}/180</CharCount>
              </FormGroup>
              
              <SubmitButton type="submit">Submit</SubmitButton>
            </form>
          </FormContainer>
          
          <InfoContainer>
            <InfoTitle>Contact Us</InfoTitle>
            <InfoSubtitle>
              Your IT Solution Starts Here - Powered by Zyntro Software Solutions Pvt Ltd. Provide
            </InfoSubtitle>
            
            <GetInTouch>Get in Touch</GetInTouch>
            
            <ContactItem>
              <ContactIcon>
                <FaPhone />
              </ContactIcon>
              <ContactText>+91-8787044997</ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaEnvelope />
              </ContactIcon>
              <ContactText>support@zyntro.in</ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>
               Registered Address: Sant Ravidas Nagar, Bhadohi, Uttar Pradesh, India, 2214013
              </ContactText>
            </ContactItem>

             <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt />
              </ContactIcon>
              <ContactText>
               Office Address: S-2/1A-81-R-I/101, Tagore Town Colony, Orderly Bazar Varanasi-221002
              </ContactText>
            </ContactItem>


          </InfoContainer>
        </ContactGrid>
      </ContentWrapper>
      
      <ChatIcon>
        <FaComments />
      </ChatIcon>
    </LoginSectionContainer>
  );
};

export default LoginSection; 